import { useState, useRef } from "react";
import { Scale, Compass, Upload, X, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { SectionHeader } from "./Home";

const INITIAL = {
  full_name: "", email: "", phone: "", profession: "", license_number: "",
  experience_years: "", city: "", state: "", districts_covered: "", message: "",
};

const PROFESSIONS = ["Advocate / Lawyer", "Property Lawyer", "Licensed Surveyor", "Title Examiner", "Due Diligence Specialist", "Retired Revenue Officer", "Other"];

export default function JoinExpertsPage() {
  const [form, setForm] = useState(INITIAL);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const document_ids = [];
      if (resume) {
        const fd = new FormData();
        fd.append("file", resume);
        const { data } = await api.post("/uploads", fd);
        document_ids.push(data.id);
      }
      await api.post("/experts", { ...form, document_ids });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="experts-success" className="py-32 md:py-44 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#113835] text-[#C1A74A] flex items-center justify-center mx-auto mb-10">
            <CheckCircle2 size={32} strokeWidth={1.25} />
          </div>
          <p className="tt-eyebrow mb-4">Application Received</p>
          <h1 className="text-3xl md:text-5xl text-[#113835] tracking-tight">Welcome to the network.</h1>
          <p className="mt-6 text-base text-gray-600 font-light leading-relaxed">
            Thank you for applying. Our empanelment team will review your credentials and contact you within 3–5 business days.
          </p>
          <button data-testid="experts-new-application" onClick={() => { setForm(INITIAL); setResume(null); setSubmitted(false); }} className="tt-btn-outline mt-12">
            Submit Another Application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="join-experts-page">
      <section className="py-24 md:py-32 bg-[#113835] tt-grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <SectionHeader
            dark
            eyebrow="Join as Experts"
            title="Lawyers. Surveyors. Due diligence specialists."
            sub="Terraa & Title works with a vetted, empanelled network of advocates, property lawyers, licensed surveyors, and due diligence specialists across Maharashtra's districts. Our customers trust our reports because of this network — every review is done by a verified professional, not an algorithm alone. If records are your craft, we would like to hear from you."
          />
          <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              [Scale, "Advocates & Lawyers", "Title opinions, litigation checks, curative drafting, and conveyancing support."],
              [Compass, "Licensed Surveyors", "Boundary demarcation, Bhunaksha map reconciliation, and site verification."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="bg-[#113835] p-8">
                <Icon size={28} strokeWidth={1} className="text-[#C1A74A]" />
                <h3 className="font-heading text-base font-medium text-white mt-5">{t}</h3>
                <p className="mt-3 text-sm text-white/65 font-light leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12">
          <div className="border border-white/15 bg-white/[0.04] p-7" data-testid="experts-vetting-note">
            <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#C1A74A] mb-2">Our vetting process</p>
            <p className="text-sm text-white/70 font-light leading-relaxed max-w-4xl">
              Every applicant is verified — bar council enrolment / license checks, experience review, and a sample
              case evaluation — before empanelment. We currently onboard experts across Maharashtra districts,
              with priority for Thane, Palghar, Raigad, Pune, and Nashik coverage.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <form onSubmit={onSubmit} data-testid="experts-form" className="space-y-10">
            <h2 className="text-3xl text-[#113835]">Apply for empanelment</h2>
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="tt-label" htmlFor="ex-name">Full Name *</label>
                <input id="ex-name" data-testid="experts-input-name" className="tt-input" required value={form.full_name} onChange={set("full_name")} placeholder="Your name" />
              </div>
              <div>
                <label className="tt-label" htmlFor="ex-email">Email Address *</label>
                <input id="ex-email" data-testid="experts-input-email" className="tt-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@practice.com" />
              </div>
              <div>
                <label className="tt-label" htmlFor="ex-phone">Phone Number *</label>
                <input id="ex-phone" data-testid="experts-input-phone" className="tt-input" required value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
              </div>
              <div>
                <label className="tt-label" htmlFor="ex-profession">Profession *</label>
                <select id="ex-profession" data-testid="experts-select-profession" className="tt-input" required value={form.profession} onChange={set("profession")}>
                  <option value="" disabled>Select profession</option>
                  {PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="tt-label" htmlFor="ex-license">Bar Council / License No.</label>
                <input id="ex-license" data-testid="experts-input-license" className="tt-input" value={form.license_number} onChange={set("license_number")} placeholder="Enrollment or license number" />
              </div>
              <div>
                <label className="tt-label" htmlFor="ex-exp">Years of Experience *</label>
                <input id="ex-exp" data-testid="experts-input-experience" className="tt-input" required value={form.experience_years} onChange={set("experience_years")} placeholder="e.g. 8" />
              </div>
              <div>
                <label className="tt-label" htmlFor="ex-city">City *</label>
                <input id="ex-city" data-testid="experts-input-city" className="tt-input" required value={form.city} onChange={set("city")} placeholder="City" />
              </div>
              <div>
                <label className="tt-label" htmlFor="ex-state">State *</label>
                <input id="ex-state" data-testid="experts-input-state" className="tt-input" required value={form.state} onChange={set("state")} placeholder="State" />
              </div>
            </div>
            <div>
              <label className="tt-label" htmlFor="ex-districts">Districts / Talukas Covered</label>
              <input id="ex-districts" data-testid="experts-input-districts" className="tt-input" value={form.districts_covered} onChange={set("districts_covered")} placeholder="e.g. Thane, Palghar, Raigad" />
            </div>
            <div>
              <label className="tt-label" htmlFor="ex-message">About Your Practice</label>
              <textarea id="ex-message" data-testid="experts-input-message" className="tt-input" value={form.message} onChange={set("message")} placeholder="Areas of expertise, notable work, availability..." />
            </div>
            <div>
              <p className="tt-label">Resume / Profile (optional — PDF, DOC, or image)</p>
              {resume ? (
                <div className="flex items-center justify-between border border-[#113835]/10 px-4 py-3 text-sm mt-2">
                  <span className="flex items-center gap-3 text-[#113835]"><FileText size={16} strokeWidth={1.25} className="text-[#C1A74A]" />{resume.name}</span>
                  <button type="button" data-testid="experts-remove-resume" onClick={() => setResume(null)} aria-label="Remove resume" className="text-gray-400 hover:text-red-600 transition-colors">
                    <X size={16} strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <button type="button" data-testid="experts-upload-trigger" onClick={() => fileRef.current?.click()}
                  className="w-full border border-dashed border-[#113835]/30 hover:border-[#C1A74A] transition-colors py-8 flex flex-col items-center gap-3 text-[#1D504C] mt-2">
                  <Upload size={24} strokeWidth={1.25} className="text-[#C1A74A]" />
                  <span className="font-heading text-xs uppercase tracking-[0.16em]">Upload resume</span>
                </button>
              )}
              <input ref={fileRef} data-testid="experts-file-input" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) setResume(e.target.files[0]); e.target.value = ""; }} />
            </div>
            <button data-testid="experts-submit-button" type="submit" disabled={submitting} className="tt-btn-gold w-full justify-center disabled:opacity-60">
              {submitting ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
